import uniqBy from 'lodash/uniqBy';
import {
  applySnapshot,
  destroy,
  flow,
  getParentOfType,
  getRoot,
  getSnapshot,
  IMSTArray,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import { AppealFile, AppealsStore } from 'store/models/appealsStore';
import { RequestStateModel } from 'store/models/general-models';
import { IRootStore } from 'store/rootStore';
import { formatDateToISO } from 'utils';
import { IWorkerNew } from 'types';
import { IAppeal, IAppealFile, IAppealUpdateData } from '../../types';
import {
  AnotherAnswerProviders,
  AppealSources,
  AppealStatuses,
} from '../../constants';
import { Appeals, Files } from 'http-requests';
import {
  AppealCreateSettingsModel,
  RateModel,
  RequesterModel,
  SubjectModel,
  UnconfirmedChangeModel,
} from './models';
import { IUserStore } from '../../../userStore';
import {
  AppealFileModel,
  AreaModel,
  EmployeeModel,
  HouseModel,
  IDNameModel,
} from '../base';

const CommentStatsModel = types
  .model('CommentStatsModel', {
    all: types.optional(types.number, 0),
    unread: types.optional(types.number, 0),
  })
  .actions((self) => ({
    increment<K extends keyof typeof self>(fieldName: K) {
      self[fieldName] += 1;
    },
    decrement<K extends keyof typeof self>(fieldName: K) {
      self[fieldName] = Math.max(0, (self[fieldName] -= 1));
    },
  }));

export const AnswerModel = types
  .model('AnswerModel', {
    _id: types.optional(types.identifier, ''),
    files: types.optional(types.array(AppealFileModel), []),
    text: types.optional(types.string, ''),
    employee: types.optional(types.maybeNull(EmployeeModel), {}),
    created_at: types.optional(types.string, ''),
    is_published: types.optional(types.maybeNull(types.boolean), null),
    source: types.optional(
      types.maybeNull(types.enumeration(['gis', 'dispatcher'] as const)),
      null
    ),
  })
  .views((self) => ({
    get isNoAnswer() {
      return self.is_published === null;
    },
  }))
  .actions((self) => ({
    deleteFile(file: IAppealFile) {
      destroy(file);
    },
    downloadFile: flow(function* (file: IAppealFile) {
      try {
        if (!file.shared_id) {
          throw new Error('Не удалось скачать файл');
        }

        file.downloadStatus.loadingStates();
        const response: any = yield Files.GetFileFromFilesMS(file.shared_id);

        if (!response.ok) {
          throw new Error(`Ошибка при загрузке файла: ${response.statusText}`);
        }

        const blob = yield response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', file.name);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        file.downloadStatus.successStates();

        return response;
      } catch (error) {
        file.downloadStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        file.downloadStatus.finallyStates();
      }
    }),
  }));

const RelationModel = types
  .model('RelationModel', {
    request_number: types.optional(types.maybeNull(types.string), null),
    chat_id: types.optional(types.maybeNull(types.number), null),
    message_id: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    get messageLink(): string | null {
      if (!self.chat_id || !self.message_id) {
        return null;
      }

      return `${window.location.origin}/house_chats/${self.chat_id}/${self.message_id}`;
    },
  }));

const LinkedRequestModel = types.model('LinkedRequestModel', {
  _id: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
});

const ObserversModel = types
  .model('ObserversModel', {
    employees: types.optional(types.array(EmployeeModel), []),
    departments: types.optional(types.array(IDNameModel), []),
  })
  .views((self) => ({
    get hasCurrentUser(): boolean {
      const currentUser = getRoot<IRootStore>(self).user;
      const allObserversIDs = this.allObservers.map((el) => el._id);

      return (
        allObserversIDs.includes(currentUser.id) ||
        allObserversIDs.includes(currentUser.department.id)
      );
    },
    get isOnlyDepartments(): boolean {
      return !self.employees.length && !!self.departments.length;
    },
    get allObservers() {
      const employeeObservers = self.employees.map((el) => ({
        _id: el._id,
        name: el.fullName,
      }));

      const usedDepartmentIds = new Set(
        self.employees.map((el) => el.department._id)
      );

      const departmentObservers = self.departments
        .filter((dept) => !usedDepartmentIds.has(dept._id))
        .map((el) => ({
          _id: el._id,
          name: el.name,
          isDepartment: true,
        }));

      return [...employeeObservers, ...departmentObservers];
    },
  }));

const ExternalControl = types.model('ExternalControl', {
  _id: types.optional(types.identifier, ''),
  number: types.optional(types.string, ''),
  first_name: types.optional(types.string, ''),
  last_name: types.optional(types.string, ''),
  patronymic_name: types.optional(types.string, ''),
  provider_id: types.optional(types.string, ''),
});

const DispatcherModel = EmployeeModel.props({
  position: types.optional(IDNameModel, {}),
  department: types.optional(IDNameModel, {}),
  external_control: types.optional(types.maybeNull(ExternalControl), null),
});

const IncomingDataModel = types
  .model('IncomingData', {
    number: types.optional(types.string, ''),
    created_at: types.optional(types.string, moment().toISOString()),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
  }));

const OurSpbModel = types.model('OurSpbModel', {
  problem_link: types.optional(types.string, ''),
  problem_number: types.optional(types.string, ''),
});

const HouseChatRefModel = types.model('HouseChatRefModel', {
  chat_id: types.number,
  max_message_id: types.string,
});

export const AppealBaseModel = types
  .model('AppealModel', {
    _id: types.optional(types.identifier, ''),
    requester: types.optional(RequesterModel, {}),
    topic: types.optional(SubjectModel, {}),
    description: types.optional(types.string, ''),
    house: types.optional(HouseModel, {}),
    area: types.optional(types.maybeNull(AreaModel), null),
    created_at: types.optional(types.string, ''),
    dispatcher: types.optional(DispatcherModel, {}),
    executor: types.optional(EmployeeModel, {}),
    relations: types.optional(RelationModel, {}),
    linked_request: types.optional(types.maybeNull(LinkedRequestModel), null),
    status: types.optional(types.enumeration(AppealStatuses), 'new'),
    _type: types.optional(types.string, 'statement'),
    requester_files: types.optional(types.array(AppealFileModel), []),
    observers: types.optional(ObserversModel, {}),
    answers: types.optional(types.array(AnswerModel), []),
    no_answer: types.optional(types.maybeNull(AnswerModel), null),
    revoked_at: types.optional(types.maybeNull(types.string), null),
    rate: types.optional(RateModel, {}),
    category_ids: types.optional(types.array(types.string), []),
    number: types.optional(types.string, ''),
    provider: types.optional(IDNameModel, {}),
    department: types.optional(IDNameModel, {}),
    source: types.optional(types.enumeration(AppealSources), 'tenant'),
    incoming_data: types.optional(IncomingDataModel, {}),
    deadline_at: types.optional(types.maybeNull(types.string), ''),
    closed_at: types.optional(types.maybeNull(types.string), ''),
    unconfirmed_change: types.optional(
      types.maybeNull(UnconfirmedChangeModel),
      null
    ),
    requester_comment: types.optional(types.maybeNull(types.string), null),
    joined_neighbors_count: types.optional(types.number, 0),
    comment_stats: types.optional(CommentStatsModel, {}),
    has_files_in_comments: types.optional(types.boolean, false),

    createSettings: types.optional(AppealCreateSettingsModel, {}),
    our_spb: types.optional(types.maybeNull(OurSpbModel), null),
    house_chat: types.optional(types.maybeNull(HouseChatRefModel), null),
  })
  .volatile((self) => ({
    initialSnapshot: null as null | typeof self,
    isNewAppeal: false as boolean,
    saveAnswerStatus: RequestStateModel.create({}),
    publicAnswerStatus: RequestStateModel.create({}),
    retrieveStatus: RequestStateModel.create({}),
    sendCommentStatus: RequestStateModel.create({}),
    updateStatus: RequestStateModel.create({}),
    acceptStatus: RequestStateModel.create({}),
    createStatus: RequestStateModel.create({}),
    convertStatus: RequestStateModel.create({}),
    noAnswerReason: RequestStateModel.create({}),
    deleteUnconfirmedChangeStatus: RequestStateModel.create({}),
  }))
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
    get userPermissions(): Record<
      'isObserver' | 'isExecutor' | 'ticketsAccess',
      boolean
    > {
      const root = getRoot<IRootStore>(self);
      const currentUserID = root.user.id;
      const currentWorker = root.workers.getById(currentUserID);

      return {
        isObserver: self.observers.hasCurrentUser,
        isExecutor: self.executor._id === currentUserID,
        ticketsAccess:
          self.executor._id === currentUserID ||
          currentWorker?.settings?.tickets_access_level === 'all' ||
          (currentWorker?.settings?.tickets_access_level === 'by_dept' &&
            self.department._id === currentWorker?.department?.id),
      };
    },
    get incomingInfo(): string | null {
      if (!self.incoming_data.number) {
        return null;
      }

      return `Входящий №${self.incoming_data.number} от ${moment(
        self.incoming_data.created_at
      ).format('DD.MM.YYYY')}`;
    },
    get requesterGreeting(): string {
      const user = getRoot<{ user: IUserStore }>(self).user;

      if (
        !self.requester.last_name ||
        !self.requester.first_name ||
        AnotherAnswerProviders.includes(user.providerId)
      ) {
        return self.requester.role.tenant
          ? 'Уважаемый собственник,'
          : 'Уважаемый заявитель,';
      }

      return `Уважаемый(-ая) ${self.requester.last_name} ${self.requester.first_name},`;
    },
    get executorSignature(): string {
      return `${self.executor.position.name} ${self.executor.shortName}`;
    },
    get answerTemplate(): string {
      return `
        <p>${this.requesterGreeting}</p>
        <p></p>
        <p></p>
        <p></p>
        <p>${this.executorSignature}</p>
      `;
    },
    get uiFlags() {
      return {
        showAcceptButton:
          !self.executor._id &&
          !this.statusFlags.isRevoked &&
          !(this.sourceFlags.fromGIS && this.statusFlags.isPerformed),
      };
    },
    get statusFlags() {
      return {
        isRevoked: self.status === 'revoked',
        isRun: self.status === 'run',
        isNew: self.status === 'new',
        isPerformed: self.status === 'performed',
      };
    },
    get sourceFlags() {
      return {
        fromDispatcher: self.source === 'dispatcher',
        fromTenant: self.source === 'tenant',
        fromGIS: self.source === 'gis',
        fromRequest: self.source === 'request',
        fromHouseChat: self.source === 'house_chat',
      };
    },
    get statusColor() {
      switch (self.status) {
        case 'new':
          return 'primary';
        case 'revoked':
          return 'gray';
        case 'run':
          return 'orange';
        default:
          return 'green';
      }
    },
    get sourceColor() {
      switch (self.source) {
        case 'gis':
          return 'green';
        case 'our_spb':
        case 'house_chat':
          return 'orange';
        default:
          return 'primary';
      }
    },
    get mainAnswer(): (typeof self.answers)[number] | null {
      return self.no_answer ?? self.answers[0] ?? null;
    },
    get publishedAddAnswers() {
      return self.answers
        .slice(self.no_answer ? 0 : 1)
        .filter((answer) => answer.is_published);
    },
    get lastUnsavedAnswer() {
      return self.answers.find((answer) => !answer.is_published) || null;
    },
    get sourceLabel(): string | null {
      if (['dispatcher', 'request'].includes(self.source)) {
        return null;
      }

      if (self.source === 'our_spb') {
        return 'Наш СПб';
      }

      if (self.source === 'house_chat') {
        return 'Домовые чаты';
      }

      return self.source === 'tenant' ? 'ЛКЖ' : 'ГИС';
    },
    get fullAddress(): string {
      return `${self.house.address.replace(/\s*(\d+)/g, '\u00A0$1')}${
        self.area?.formatted_number
          ? `, ${self.area?.formatted_number.replace(/\s*(\d+)/g, '\u00A0$1')}`
          : ''
      }`;
    },
    get disabledTabsStatus(): Record<
      typeof self.createSettings.selectedTab,
      boolean
    > {
      let descriptionDisabled: boolean = false;
      let executorDisabled: boolean = false;

      if (self.source === 'our_spb') {
        descriptionDisabled =
          !self.createSettings.isPortalProblemLinkValid ||
          !self.createSettings.selectedHouseID;
      } else {
        switch (self.createSettings.appealer.contactType) {
          case 'tenant':
            descriptionDisabled = self.createSettings.anotherMember
              ? !self.createSettings.appealer.isValidOtherTenant
              : !self.createSettings.appealer.memberId;

            break;
          case 'worker':
            descriptionDisabled =
              !self.createSettings.appealer.workerId ||
              !self.createSettings.selectedHouseID;

            break;
          case 'other':
            if (self.createSettings.appealer.thirdPartyType === 'physical') {
              descriptionDisabled = !self.createSettings.appealer.physicalId;
            } else {
              descriptionDisabled = !self.createSettings.appealer.legalId;
            }

            descriptionDisabled =
              descriptionDisabled || !self.createSettings.selectedHouseID;

            break;
          default:
            break;
        }
      }

      executorDisabled =
        descriptionDisabled ||
        !self.createSettings.subject.name.trim() ||
        self.createSettings.subject.name.trim().length < 3 ||
        !self._type.length ||
        !self.description.trim() ||
        self.description.trim().length < 3;

      return {
        appealer: false,
        description: descriptionDisabled,
        executor: executorDisabled,
      };
    },
    get nextButtonDisabledStatus(): boolean {
      if (self.createSettings.selectedTab === 'appealer') {
        return this.disabledTabsStatus.description;
      }

      if (self.createSettings.selectedTab === 'description') {
        return this.disabledTabsStatus.executor;
      }

      if (self.createSettings.selectedTab === 'executor') {
        return (
          self.createSettings.lockUploadAppealFiles ||
          (!!self.createSettings.executor.departments.length &&
            !self.createSettings.executor.workers.length)
        );
      }

      return false;
    },
    get createdAtDate() {
      return `${moment(self.created_at).format(
        this.sourceFlags.fromGIS ? 'DD.MM.YYYY' : 'DD.MM.YYYY/HH:mm'
      )}`;
    },
  }))
  .actions((self) => ({
    updateSelf(newAppeal: IAppeal) {
      const processedSnapshot = AppealModel.create(newAppeal);
      applySnapshot(self, processedSnapshot.snapshot);
    },
    retrive: flow(function* () {
      try {
        self.retrieveStatus.loadingStates();
        const response = yield Appeals.GetAppealByID(self._id);

        const processedSnapshot = AppealModel.create(response);
        applySnapshot(self, processedSnapshot.snapshot);
        self.retrieveStatus.successStates();

        return self;
      } catch (error) {
        self.retrieveStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.retrieveStatus.finallyStates();
      }
    }),
  }))
  .actions((self) => ({
    afterCreate() {
      self.initialSnapshot = self;
    },
    updateField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
    updateArrayField<K extends keyof typeof self>(
      fieldName: K extends K
        ? (typeof self)[K] extends IMSTArray<any>
          ? K
          : never
        : never,
      value: any[]
    ) {
      const field = self[fieldName];

      if (Array.isArray(field)) {
        (field as IMSTArray<any>).replace(value);
      }
    },
    updateCommentsStats() {
      const parent = getParentOfType(self, AppealsStore);
      self.comment_stats.all = parent.comments.length;
    },
    toggleNewAppeal() {
      self.isNewAppeal = !self.isNewAppeal;
    },
    markNew() {
      this.toggleNewAppeal();

      setTimeout(() => {
        this.toggleNewAppeal();
      }, 2000);
    },
    reset() {
      applySnapshot(self, {});
    },
    create: flow(function* () {
      try {
        self.createStatus.loadingStates();
        const baseParams = {
          requester_files: self.createSettings.selectedFiles
            .filter((file) => !!file?.file_id)
            .map((file) => ({
              _id: file.file_id!,
              name: file.file.name,
            })),
          topic: {
            _id: self.createSettings.subject._id || null,
            name: self.createSettings.subject.name || null,
          },
          house: {
            _id: self.createSettings.selectedHouseID,
          },
          area: self.createSettings.selectedAreaID
            ? { _id: self.createSettings.selectedAreaID }
            : null,
          description: self.description,
          _type: self._type,
          executor: self.createSettings.formatExecutor,
          observers: self.createSettings.formatObservers,
          category_ids: self.category_ids,
          incoming_data: self.incoming_data.number
            ? {
                ...self.incoming_data,
                number: self.incoming_data.number.trim(),
              }
            : null,
          deadline_at: formatDateToISO(
            self.createSettings.subject.deadlineDate
          ),
        };

        // Общий requester для всех веток, кроме our_spb (там requester —
        // заглушка, игнорируется сервером).
        const requester = {
          _id:
            self.createSettings.appealer.contactType === 'tenant'
              ? self.createSettings.appealer.memberId
              : self.createSettings.appealer.contactType === 'worker'
                ? self.createSettings.appealer.workerId
                : self.createSettings.appealer.thirdPartyType === 'physical'
                  ? self.createSettings.appealer.physicalId
                  : self.createSettings.appealer.legalId,
          _type:
            self.createSettings.appealer.contactType === 'tenant'
              ? 'tenant'
              : self.createSettings.appealer.contactType === 'worker'
                ? 'employee'
                : self.createSettings.appealer.thirdPartyType === 'physical'
                  ? 'other_person'
                  : 'other_employee',
        };

        // Заглушка requester для our_spb — значение игнорируется сервером.
        const ourSpbRequester: {
          _id: string;
          _type: string;
          phone_number?: { type: string; number: string };
        } = {
          _id: '000000000000000000000000',
          _type: 'tenant',
          phone_number: {
            type: 'cell',
            number: self.createSettings.phone,
          },
        };

        if (ourSpbRequester.phone_number?.number === '') {
          delete ourSpbRequester.phone_number;
        }

        const params =
          self.source === 'our_spb'
            ? {
                ...baseParams,
                requester: ourSpbRequester,
                our_spb: {
                  problem_link: self.createSettings.portalProblemLink.trim(),
                },
              }
            : self.source === 'house_chat'
              ? {
                  // source для обращения не передаётся вообще — источник
                  // бэк выводит из наличия блока house_chat (как our_spb
                  // выше не шлёт source, только свой блок).
                  ...baseParams,
                  house_chat: {
                    chat_id: self.relations.chat_id,
                    max_message_id: self.relations.message_id,
                  },
                  requester,
                }
              : {
                  ...baseParams,
                  source: self.source,
                  requester,
                };

        const response = yield Appeals.CreateAppeal(params);
        self.createStatus.successStates();

        return response;
      } catch (error) {
        self.createStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.createStatus.finallyStates();
      }
    }),
    update: flow(function* (data: Partial<IAppealUpdateData>) {
      try {
        self.updateStatus.loadingStates();
        const response = yield Appeals.UpdateAppeal(self._id, data);
        self.updateSelf(response);
        self.updateStatus.successStates();

        return self;
      } catch (error) {
        self.updateStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.updateStatus.finallyStates();
      }
    }),
    accept: flow(function* () {
      try {
        self.acceptStatus.loadingStates();
        const response = yield Appeals.AcceptAppeal(self._id);
        self.updateSelf(response);
        self.acceptStatus.successStates();

        return self;
      } catch (error) {
        self.acceptStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.acceptStatus.finallyStates();
      }
    }),
    convertToRequest: flow(function* () {
      try {
        self.convertStatus.loadingStates();
        const response = yield Appeals.ConvertToRequest(self._id);
        self.updateSelf(response);
        self.convertStatus.successStates();

        return self;
      } catch (error) {
        self.convertStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.convertStatus.finallyStates();
      }
    }),
    saveAnswer: flow(function* (
      text: string,
      files?: { file_id: string; shared_id: string; name: string }[]
    ) {
      try {
        self.saveAnswerStatus.loadingStates();
        const cleanedText = text.replace(/&nbsp;/g, ' ');

        const data = {
          text: cleanedText,
          files: uniqBy(
            [
              ...(self.lastUnsavedAnswer?.files ?? []).map((file) => ({
                _id: file._id,
                name: file.name,
              })),
              ...(files ?? []).map((file) => ({
                _id: file.file_id,
                name: file.name,
              })),
            ],
            '_id'
          ),
        };

        const response = self.lastUnsavedAnswer
          ? yield Appeals.UpdateSavedAppealAnswer(
              self._id,
              self.lastUnsavedAnswer._id,
              data
            )
          : yield Appeals.SaveAppealAnswer(self._id, data);

        self.updateSelf(response);
        self.saveAnswerStatus.successStates();

        return response;
      } catch (error) {
        self.saveAnswerStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.saveAnswerStatus.finallyStates();
      }
    }),
    publishAppeal: flow(function* (answerID: string | null) {
      try {
        if (!answerID) {
          throw new Error();
        }

        self.publicAnswerStatus.loadingStates();
        const response = yield Appeals.PublishAppealAnswer(self._id, answerID);

        self.updateSelf(response);
        self.publicAnswerStatus.successStates();

        return self;
      } catch (error) {
        self.publicAnswerStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.publicAnswerStatus.finallyStates();
      }
    }),
    sendComment: flow(function* (
      text: string,
      files?: { file_id: string; shared_id: string; name: string }[]
    ) {
      try {
        self.sendCommentStatus.loadingStates();
        const response = yield Appeals.SendComment(self._id, {
          text,
          files: files?.map((file) => ({
            _id: file.file_id,
            name: file.name,
          })),
        });
        yield self.retrive();
        self.sendCommentStatus.successStates();

        return response;
      } catch (error) {
        self.sendCommentStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.sendCommentStatus.finallyStates();
      }
    }),
    sendNoAnswerReason: flow(function* (data: { text: string }) {
      try {
        self.noAnswerReason.loadingStates();
        const response = yield Appeals.NoAnwerReason(self._id, data);
        self.updateSelf(response);
        self.noAnswerReason.successStates();
      } catch (error) {
        self.noAnswerReason.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.noAnswerReason.finallyStates();
      }
    }),
    uploadFiles: flow(function* (files: Array<AppealFile>) {
      try {
        yield Promise.all(
          files
            .filter((file) => file.shared_id)
            .map(async (file) => {
              const formData = new FormData();
              formData.append('file', file.file);

              await Files.SaveFileToFilesMS(file.shared_id!, formData);
            })
        );

        return files;
      } catch (e) {
        console.error('AppealsStore: AppealBaseModel promise uploadFiles down');

        return Promise.reject(e);
      }
    }),
    downloadFile: flow(function* (file: IAppealFile) {
      try {
        if (!file.shared_id) {
          throw new Error('Не удалось скачать файл');
        }

        file.downloadStatus.loadingStates();
        const response = yield Files.GetFileFromFilesMS(file.shared_id);

        if (!response.ok) {
          throw new Error(`Ошибка при загрузке файла: ${response.statusText}`);
        }

        const blob = yield response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', file.name);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        file.downloadStatus.successStates();

        return response;
      } catch (error) {
        file.downloadStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        file.downloadStatus.finallyStates();
      }
    }),
    deleteUnconfirmedChange: flow(function* () {
      try {
        self.deleteUnconfirmedChangeStatus.loadingStates();
        const response = yield Appeals.DeleteUnconfirmedChange(self._id);
        self.deleteUnconfirmedChangeStatus.successStates();
        self.updateSelf(response);

        return self.snapshot;
      } catch (error) {
        self.deleteUnconfirmedChangeStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.deleteUnconfirmedChangeStatus.finallyStates();
      }
    }),
  }))
  .actions((self) => ({
    // Аналог того, что делает searchAppealerByPhone (contactType='worker' +
    // workers/workerId), но без сетевого поиска (getWorkersByPhone) —
    // сотрудник тут уже точно известен (это сам залогиненный пользователь),
    // а поиск по телефону мог бы подставить другого человека с тем же
    // рабочим номером.
    initFromHouseChatMessage(params: {
      houseId: string;
      description: string;
      employee: IWorkerNew;
      messageId: string;
      chatId: number;
    }) {
      self.createSettings.appealer.setContactType('worker');
      self.createSettings.appealer.setWorkers([params.employee]);
      self.createSettings.appealer.setSelectedId(
        'workerId',
        params.employee.id
      );
      self.createSettings.markAppealerSearchStarted();
      self.createSettings.updateField('selectedHouseID', params.houseId);
      self.updateField('description', params.description);
      self.updateField('source', 'house_chat');
      self.relations.chat_id = params.chatId;
      self.relations.message_id = params.messageId;
    },
  }));

export const AppealModel = types.snapshotProcessor(AppealBaseModel, {
  preProcessor(sn: Partial<IAppeal>) {
    const rawRequester = sn.requester as
      | (Partial<IAppeal['requester']> & {
          phone_numbers?: Array<{
            _type?: string;
            type?: string;
            number?: string;
          }>;
        })
      | undefined;

    const normalizedRequester = rawRequester
      ? {
          ...rawRequester,
          first_name: rawRequester.first_name ?? '',
          last_name: rawRequester.last_name ?? '',
          patronymic_name: rawRequester.patronymic_name ?? null,
          phone_numbers: (rawRequester.phone_numbers ?? []).map((phone) => ({
            ...phone,
            _type: phone._type ?? (phone as { type?: string }).type ?? 'cell',
          })),
        }
      : rawRequester;

    return {
      ...sn,
      requester: normalizedRequester,
      incoming_data: sn.incoming_data ?? {},
      dispatcher: sn.dispatcher ?? {},
      executor: sn.executor ?? {},
      relations: sn.relations ?? {},
      rate: sn.rate ?? {},
      linked_request: sn.linked_request ?? null,
    };
  },
  postProcessor(sn: IAppeal) {
    const { createSettings, ...rest } = sn;

    return rest;
  },
});
